import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useApp, useFetch } from '../store.tsx';
import { Card, CopyableSecret, Sheet, Spinner } from '../components.tsx';
import { IngestActivity } from './IngestActivity.tsx';
import * as native from '../native.ts';
import { onUpdateReady, isUpdatePending, applyUpdate } from '../updates.ts';

interface Source {
  id: string; type: string; label: string | null; has_key: boolean; event_count: number;
}

const SHORTCUT_LABEL = 'iPhone Shortcut';

/** The source types a phone posts through. CSV and manual have no device. */
const DEVICE_TYPES = ['sms', 'notification'];

/**
 * "A new version is ready."
 *
 * For the case the automatic path cannot cover: the app is open and in use
 * when a deploy lands. Reloading underneath someone mid-sentence is worse than
 * asking, so this offers rather than acts — while a return from the background,
 * where nothing can be half typed, applies it without asking.
 */
export function UpdateReadyBanner(): JSX.Element | null {
  const { locale } = useApp();
  const ar = locale === 'ar';
  const [ready, setReady] = useState(isUpdatePending());

  useEffect(() => onUpdateReady(() => setReady(true)), []);
  if (!ready) return null;

  return (
    <div className="banner-slot">
      <button className="banner banner--update" onClick={applyUpdate}>
        <span className="banner__icon" aria-hidden>↑</span>
        <span className="banner__text">
          <span className="banner__title">{ar ? 'يتوفر إصدار جديد' : 'A new version is ready'}</span>
          <span className="banner__sub">
            {ar
              ? 'اضغط لإعادة التحميل. يحدث هذا تلقائياً في المرة القادمة التي تفتح فيها التطبيق.'
              : 'Tap to reload. This happens on its own the next time you open the app.'}
          </span>
        </span>
        <span className="banner__chevron flip" aria-hidden>›</span>
      </button>
    </div>
  );
}

/**
 * A standing reminder that the phone was never connected.
 *
 * Keyed on whether anything has ever arrived, not on whether "I'll do this
 * later" was tapped. Remembering the tap would miss the more common way people
 * end up here — tapping Done to get off the screen without doing the work —
 * and would need clearing afterwards, which is one more thing to get wrong.
 * Nothing having arrived is the condition that actually matters, it covers
 * every route into it, and it clears itself the moment the first message
 * lands.
 *
 * Not dismissible while it is true. A reminder you can permanently wave away
 * is not a reminder, and the thing it is warning about is not cosmetic: an
 * unconnected phone means the app receives nothing at all, and an app showing
 * nothing looks broken rather than unconfigured.
 */
export function PhoneNotConnectedBanner(): JSX.Element | null {
  const { locale, user } = useApp();
  const ar = locale === 'ar';
  const [open, setOpen] = useState(false);
  const { data, reload } = useFetch<{ sources: Source[] }>('/sources');

  const devices = data?.sources.filter((s) => DEVICE_TYPES.includes(s.type)) ?? [];
  // Any one of them working is enough — a spare key minted for a second bank
  // and never used is not a broken setup.
  const connected = devices.some((s) => s.event_count > 0);
  if (!data || devices.length === 0 || connected) return null;

  return (
    <>
      <div className="banner-slot">
        <button className="banner" onClick={() => setOpen(true)}>
          <span className="banner__icon" aria-hidden>!</span>
          <span className="banner__text">
            <span className="banner__title">
              {ar ? 'هاتفك غير مرتبط بعد' : 'Your phone isn’t connected yet'}
            </span>
            {/* Says how it ends. A warning with no stated exit reads as
                permanent, and someone who has just finished setup needs to
                know this clears on its own rather than wondering what else
                they were supposed to press. */}
            <span className="banner__sub">
              {ar
                ? 'لن تصل أي حركات قبل إكمال الإعداد. يختفي هذا التنبيه من تلقاء نفسه فور وصول أول حركة.'
                : 'No transactions can arrive until setup is done. This clears itself the moment the first one lands.'}
            </span>
          </span>
          <span className="banner__chevron flip" aria-hidden>›</span>
        </button>
      </div>
      {open && (
        <ConnectPhoneSheet
          platform={user?.devicePlatform ?? 'ios'}
          onClose={() => { setOpen(false); reload(); }}
        />
      )}
    </>
  );
}

/**
 * Connecting a phone, for both platforms.
 *
 * The same body serves two places, deliberately. It is the last step of signup,
 * shown the moment the account exists, and it is the screen in Settings people
 * come back to when nothing is arriving. One implementation, so the second
 * visit says the same thing as the first.
 *
 * Signup issues the key. This screen is where it is *shown*, wrapped in the
 * instructions that say what to do with it — because a secret displayed once
 * with nothing around it is a secret about to be lost. Before this, signup
 * printed the key on a screen of its own and then sent the user to Settings,
 * where the only available action was "Replace key": copy the first key into a
 * Shortcut, tap replace, and the Shortcut silently stops working.
 */
export function ConnectPhoneBody(
  { platform, issuedKey, onDone }: {
    platform: 'ios' | 'android' | null;
    /** The key signup just minted, shown here rather than on a screen of its own. */
    issuedKey?: string | null;
    onDone?: () => void;
  },
): JSX.Element {
  return platform === 'android'
    ? <AndroidBody issuedKey={issuedKey} onDone={onDone} />
    : <IphoneBody issuedKey={issuedKey} onDone={onDone} />;
}

export function ConnectPhoneSheet(
  { platform, onClose }: { platform: 'ios' | 'android' | null; onClose: () => void },
): JSX.Element {
  const { locale } = useApp();
  const ar = locale === 'ar';
  return (
    <Sheet
      title={platform === 'android'
        ? (ar ? 'الإعداد على أندرويد' : 'Set up on Android')
        : (ar ? 'الإعداد على الآيفون' : 'Set up on iPhone')}
      onClose={onClose}
    >
      <ConnectPhoneBody platform={platform} onDone={onClose} />
    </Sheet>
  );
}

/**
 * iPhone setup.
 *
 * iOS gives no app any way to read SMS, so the automatic route is a Shortcut
 * the user builds themselves. That means the app's job here is to hand over the
 * two values the Shortcut needs and be honest about what iOS will and will not
 * do unattended — overselling "fully automatic" is how this feature earns
 * one-star reviews on a device whose OS makes the decision.
 */
function IphoneBody(
  { issuedKey, onDone }: { issuedKey?: string | null; onDone?: () => void },
): JSX.Element {
  // In the App Store build the whole URL-and-headers dance is replaced by a
  // single Shortcuts action, so most of these instructions describe work the
  // user no longer has to do.
  const isNativeApp = native.isNative();
  const { t, locale, toast, toastError } = useApp();
  const [minted, setMinted] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ar = locale === 'ar';
  // The JSON endpoint, not the plain-text one. Shortcuts offers JSON first in
  // its "Request Body" control, and a JSON field there is named by the user —
  // so instructions that say "Text" get read as a field name. Naming the field
  // the endpoint actually expects removes the ambiguity entirely.
  const endpoint = `${window.location.origin}/api/v1/ingest/event`;

  const { data: sources, reload } = useFetch<{ sources: Source[] }>('/sources');
  const existing = sources?.sources.find((s) => s.label === SHORTCUT_LABEL && s.has_key);

  // Whatever key this visit is entitled to show: the one signup just made, or
  // one minted here. On a return visit there is none, and there must not be —
  // the key exists but its plaintext is gone, which is the point of storing
  // only a hash.
  const key = issuedKey ?? minted;

  const createKey = async (mode: 'replace' | 'additional' = 'replace') => {
    setBusy(true);
    try {
      // Replacing rotates the existing key, which is what someone with a lost
      // or reset phone wants. Adding mints a separate source, which is what a
      // *second bank* in its own Messages thread needs — several cards at one
      // bank do not, since the card number in each message already sorts them.
      const res = existing && mode === 'replace'
        ? await api.post<{ ingestKey: string }>(`/sources/${existing.id}/rotate-key`)
        : await api.post<{ ingestKey?: string }>('/sources', {
            type: 'sms',
            label: mode === 'additional'
              ? `${SHORTCUT_LABEL} ${(sources?.sources.filter((s) => s.label?.startsWith(SHORTCUT_LABEL)).length ?? 0) + 1}`
              : SHORTCUT_LABEL,
          });

      if (res.ingestKey) { setMinted(res.ingestKey); reload(); }
      else toast(t('somethingWrong'), 'error');
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      {/* Lead with the constraint. Someone who installed this expecting
          Android-style SMS reading needs to know immediately. */}
      <Card style={{ background: 'var(--bg-sunken)' }}>
        <div className="small" style={{ lineHeight: 1.6 }}>
          <span className="strong">
            {ar ? 'لا يمكن لأي تطبيق قراءة رسائلك على iOS.' : 'No app can read your SMS on iOS.'}
          </span>{' '}
          {ar
            ? 'هذا قرار من آبل ولا توجد طريقة للالتفاف عليه. لكن يمكنك إعداد اختصار (Shortcut) يرسل رسائل بنكك تلقائياً — الخطوات أدناه.'
            : 'That is an Apple platform decision with no workaround. What you can do is build a Shortcut that forwards your bank messages automatically — the steps are below.'}
        </div>
      </Card>

      {/* ── The key ──────────────────────────────────────────────────────────
          Always present, in one of three states, and never gating the steps
          below it. The instructions used to render only once a key had just
          been minted, so a user who already had one — which, since signup
          started issuing them, is everyone — could not read step 2 without
          first tapping "Replace key" and invalidating the key they were
          holding. */}
      {key ? (
        <>
          <Step n={1} title={ar ? 'مفتاح الجهاز ✓' : 'Device key ✓'}>
            {ar ? 'انسخه الآن — لن يُعرض مرة أخرى.' : 'Copy it now — it will not be shown again.'}
          </Step>
          {isNativeApp ? (
            /* The key went straight into the shared keychain when it was
               created, so there is nothing to copy and nothing to lose.
               Showing it anyway would invite someone to paste it into a
               Shortcut that does not need it. */
            <Card style={{ background: 'var(--bg-sunken)' }}>
              <div className="tiny" style={{ lineHeight: 1.6 }}>
                {ar
                  ? 'تم حفظ المفتاح داخل التطبيق. لا شيء لنسخه.'
                  : 'The key is stored inside the app. Nothing to copy.'}
              </div>
            </Card>
          ) : (
            <Card>
              <CopyableSecret value={key} label={ar ? 'مفتاح الجهاز' : 'Device key'} />
            </Card>
          )}
        </>
      ) : existing ? (
        <>
          <Step n={1} title={ar ? 'مفتاح الجهاز ✓' : 'Device key ✓'}>
            {ar
              ? 'أُنشئ عند التسجيل وعُرض مرة واحدة. لا يمكن عرضه ثانية — نحفظ بصمته فقط.'
              : 'Made at signup and shown once. It cannot be shown again — only its fingerprint is stored.'}
          </Step>
          <Card style={{ borderColor: 'var(--warning)' }}>
            <div className="tiny" style={{ lineHeight: 1.6 }}>
              {ar
                ? '"استبدال المفتاح" يُبطل القديم فوراً ويوقف أي اختصار يستخدمه — استخدمه إن فقدت المفتاح أو الهاتف. لا تحتاج مفتاحاً لكل بطاقة: رقم البطاقة في كل رسالة يوزّعها تلقائياً. المفتاح الإضافي لبنك آخر برسائل منفصلة فقط.'
                : 'Replacing invalidates the old key immediately and stops any Shortcut still using it — do that only if you lost the key or the phone. You do not need a key per card: the card number in each message already sorts them. An additional key is only for a second bank with its own Messages thread.'}
            </div>
          </Card>
          <button className="btn btn--secondary btn--block" disabled={busy} onClick={() => createKey('replace')}>
            {busy ? <Spinner /> : (ar ? 'استبدال المفتاح' : 'Replace key')}
          </button>
          <button className="btn btn--ghost btn--block" disabled={busy} onClick={() => createKey('additional')}>
            {ar ? 'مفتاح إضافي لبنك آخر' : 'Additional key for another bank'}
          </button>
        </>
      ) : (
        <>
          <Step n={1} title={ar ? 'احصل على مفتاح الجهاز' : 'Get your device key'}>
            {ar
              ? 'يُعرض مرة واحدة فقط. يمكنه إرسال المعاملات فقط — لا يمكنه قراءة سجلك المالي، لذا فقدان الهاتف لا يكشف بياناتك.'
              : 'Shown once. It can only post transactions — it cannot read your financial history, so a lost phone does not expose your data.'}
          </Step>
          <button className="btn btn--primary btn--block" disabled={busy} onClick={() => createKey('replace')}>
            {busy ? <Spinner /> : (ar ? 'إنشاء مفتاح' : 'Create a key')}
          </button>
        </>
      )}

      {/* ── Building the Shortcut ────────────────────────────────────────── */}
      {isNativeApp ? (
        <>
          <Step n={2} title={ar ? 'شغّله تلقائياً' : 'Make it automatic'}>
            {ar
              ? 'الاختصارات ← الأتمتة ← + ← رسالة ← "تحتوي على" اسم بنكك، ثم أضف إجراء "Add to Fintrack" واجعل مدخله نص الرسالة.'
              : 'Shortcuts → Automation → + → Message → "contains" your bank\'s name, then add the "Add to Fintrack" action and pass it the message text.'}
          </Step>
          <Card style={{ background: 'var(--bg-sunken)' }}>
            <div className="tiny" style={{ lineHeight: 1.6 }}>
              {ar
                ? 'إجراء واحد بحقل واحد. لا رابط، ولا ترويسات، ولا JSON.'
                : 'One action with one field. No URL, no headers, no JSON to get right.'}
            </div>
          </Card>
          <Card style={{ borderColor: 'var(--warning)' }}>
            <div className="tiny" style={{ lineHeight: 1.6 }}>
              {ar
                ? 'تختلف إصدارات iOS في السماح بتشغيل أتمتة الرسائل دون تأكيد. إن طلب منك التأكيد، فهي نقرة واحدة بدلاً من صفر.'
                : 'iOS versions differ on whether a Message automation may run without confirming. If yours asks, that is one tap instead of zero.'}
            </div>
          </Card>
        </>
      ) : (
        <>
          <Step n={2} title={ar ? 'أنشئ الاختصار' : 'Build the Shortcut'}>
            {ar ? 'في تطبيق الاختصارات: + ← إضافة إجراء ← Get Contents of URL، ثم اضبط:' : 'In the Shortcuts app: + → Add Action → Get Contents of URL, then set:'}
          </Step>
          <Card className="card--flush">
            <Row label="URL" value={endpoint} mono />
            <Row label={ar ? 'الطريقة' : 'Method'} value="POST" mono />
            <Row label="Header" value="X-Ingest-Key → your key" mono />
            <Row label="Header" value="Content-Type → application/json" mono />
            <Row label={ar ? 'المحتوى' : 'Request Body'} value="JSON" mono />
            <Row label={ar ? 'الحقل' : 'Field'} value="body → Shortcut Input" mono />
          </Card>
          {/* The URL is not a secret and is needed to build the Shortcut, so it
              is copyable rather than only readable in the table above. */}
          <Card>
            <CopyableSecret value={endpoint} label={ar ? 'الرابط' : 'URL'} />
          </Card>
          {/* The field name is the one thing here that silently produces
              nothing when wrong, so it gets its own line rather than being
              left to be inferred from the table. */}
          <Card style={{ background: 'var(--bg-sunken)' }}>
            <div className="tiny" style={{ lineHeight: 1.6 }}>
              {ar
                ? 'اسم الحقل هو body بأحرف صغيرة، ونوعه Text، وقيمته Shortcut Input. أضف أيضاً إجراء "Show Notification" بعد الطلب حتى ترى النتيجة — الاختصار لا يعرض الرد إلا إذا طلبت ذلك.'
                : 'The field must be named body — lowercase — with type Text and Shortcut Input as its value. Add a Show Notification action after the request too, so you can see the result: a Shortcut shows you nothing back unless you ask it to.'}
            </div>
          </Card>

          <Step n={3} title={ar ? 'شغّله تلقائياً' : 'Make it automatic'}>
            {ar
              ? 'الاختصارات ← الأتمتة ← + ← رسالة ← "تحتوي على" اسم بنكك، ثم اختر الاختصار أعلاه. حدّد "تشغيل فوراً" إن كان متاحاً في إصدار iOS لديك.'
              : 'Shortcuts → Automation → + → Message → "contains" your bank\'s name or shortcode, then run the shortcut above. Choose Run Immediately if your iOS version offers it.'}
          </Step>

          {/* The version dependency is real; saying so here costs a little
              polish and buys the user not feeling misled later. */}
          <Card style={{ borderColor: 'var(--warning)' }}>
            <div className="tiny" style={{ lineHeight: 1.6 }}>
              {ar
                ? 'تختلف إصدارات iOS في السماح بتشغيل أتمتة الرسائل دون تأكيد. إن طلب منك التأكيد، فهي نقرة واحدة بدلاً من صفر. البديل الذي يعمل دائماً: فعّل "إظهار في ورقة المشاركة" في الاختصار، ثم شارك أي رسالة بنكية معه.'
                : 'iOS versions differ on whether a Message automation may run without confirming. If yours asks, that is one tap instead of zero. The alternative that always works: enable "Show in Share Sheet" on the shortcut, then share any bank message to it.'}
            </div>
          </Card>
        </>
      )}

      <Verify stepNumber={isNativeApp ? 3 : 4} onDone={onDone} />
    </div>
  );
}

/**
 * Android setup.
 *
 * The listener app is written but has never been compiled or published — see
 * docs/LIMITATIONS.md — so this screen does not pretend otherwise. Sending a
 * user to an app store for something that is not in it is worse than saying
 * there is nothing there yet, because they will look, fail, and conclude the
 * product is broken rather than unfinished.
 *
 * What does work today is the endpoint itself, which any forwarding app can
 * post to, and pasting a message by hand. Both are offered.
 */
function AndroidBody(
  { issuedKey, onDone }: { issuedKey?: string | null; onDone?: () => void },
): JSX.Element {
  const { locale } = useApp();
  const ar = locale === 'ar';
  const endpoint = `${window.location.origin}/api/v1/ingest/event`;

  return (
    <div className="stack">
      <Card style={{ borderColor: 'var(--warning)' }}>
        <div className="small" style={{ lineHeight: 1.6 }}>
          <span className="strong">
            {ar ? 'تطبيق أندرويد لم يُنشر بعد.' : 'The Android app is not published yet.'}
          </span>{' '}
          {ar
            ? 'نفضّل قول ذلك على إرسالك إلى متجر لا يحتويه. حتى ذلك الحين يعمل كل شيء آخر، والطريقتان أدناه تُدخلان حركاتك اليوم.'
            : 'We would rather say so than send you to a store that does not have it. Everything else works in the meantime, and the two routes below get your transactions in today.'}
        </div>
      </Card>

      {issuedKey && (
        <>
          <Step n={1} title={ar ? 'مفتاح الجهاز ✓' : 'Device key ✓'}>
            {ar
              ? 'انسخه الآن — لن يُعرض مرة أخرى. ستحتاجه عند نشر التطبيق، أو الآن مع أي تطبيق تحويل رسائل.'
              : 'Copy it now — it will not be shown again. You will need it when the app ships, or now with any SMS-forwarding app.'}
          </Step>
          <Card>
            <CopyableSecret value={issuedKey} label={ar ? 'مفتاح الجهاز' : 'Device key'} />
          </Card>
        </>
      )}

      <Step n={issuedKey ? 2 : 1} title={ar ? 'أي تطبيق تحويل رسائل' : 'Any SMS-forwarding app'}>
        {ar
          ? 'وجّهه إلى الرابط أدناه بترويسة X-Ingest-Key تحمل مفتاحك، ونص الرسالة في حقل اسمه body.'
          : 'Point it at the URL below with an X-Ingest-Key header carrying your key, and the message text in a field named body.'}
      </Step>
      <Card>
        <CopyableSecret value={endpoint} label={ar ? 'الرابط' : 'URL'} />
      </Card>

      <Step n={issuedKey ? 3 : 2} title={ar ? 'أو الصق رسالة' : 'Or paste a message'}>
        {ar
          ? 'الحركات ← إضافة حركة ← الصق رسالة بنكية. يعمل الآن دون أي إعداد، ويعرض لك ما قرأه قبل الحفظ.'
          : 'Transactions → Add transaction → Paste a bank message. Works right now with no setup, and shows what it read before saving.'}
      </Step>

      <Verify stepNumber={issuedKey ? 4 : 3} onDone={onDone} />
    </div>
  );
}

/**
 * The step people actually need after following the instructions.
 *
 * Without it, "nothing happened" is indistinguishable from "the message
 * arrived and could not be read".
 */
function Verify({ stepNumber, onDone }: { stepNumber: number; onDone?: () => void }): JSX.Element {
  const { t, locale } = useApp();
  const ar = locale === 'ar';
  return (
    <>
      <Step n={stepNumber} title={ar ? 'تحقق قبل أن تثق به' : 'Check it before trusting it'}>
        {ar
          ? 'الصق رسالة حقيقية في: الحركات ← إضافة حركة ← الصق رسالة بنكية. سيعرض لك بالضبط ما قرأه قبل حفظ أي شيء.'
          : 'Paste a real message into Transactions → Add transaction → Paste a bank message. It shows exactly what was read before saving anything.'}
      </Step>

      <div className="divider" style={{ marginBlock: 4 }} />
      <div>
        <div className="small strong">{ar ? 'ماذا وصل حتى الآن' : 'What has arrived so far'}</div>
        <div className="tiny muted" style={{ marginTop: 3, marginBottom: 10, lineHeight: 1.55 }}>
          {ar
            ? 'أرسل رسالة اختبار، ثم افتح هذه القائمة.'
            : 'Send a real message through, then check here.'}
        </div>
        <IngestActivity />
      </div>

      {onDone && (
        <button className="btn btn--primary btn--block" onClick={onDone}>{t('done')}</button>
      )}
    </>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="row" style={{ gap: 12, alignItems: 'flex-start', marginTop: 4 }}>
      <span
        aria-hidden
        style={{
          flexShrink: 0, width: 24, height: 24, borderRadius: 99,
          background: 'var(--accent-soft)', color: 'var(--accent-text)',
          display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
        }}
      >
        {n}
      </span>
      <span className="grow">
        <span className="small strong" style={{ display: 'block' }}>{title}</span>
        <span className="tiny muted" style={{ display: 'block', marginTop: 3, lineHeight: 1.6 }}>{children}</span>
      </span>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="row row--between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', gap: 12 }}>
      <span className="tiny muted" style={{ flexShrink: 0 }}>{label}</span>
      <span className={`tiny truncate${mono ? ' mono' : ''}`} style={{ textAlign: 'end' }} dir="ltr">{value}</span>
    </div>
  );
}
